import { Link } from "react-router-dom";
import { buttonVariants } from "../../components/ui/button";
import { useUpcomingAppointments } from "../../lib/hooks/doctors/use-upcoming-appointments";
import { format, isAfter } from "date-fns";
import { Video, Hospital } from 'lucide-react'; // Import icons from lucide-react

function Badge({
  children,
  variant = "default",
  size = "sm"
}: {
  children: React.ReactNode;
  variant?: "default" | "success" | "warning" | "error" | "info";
  size?: "sm" | "md";
}) {
  const baseClasses = "inline-flex items-center gap-1 rounded-full font-medium";

  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-1.5 text-sm"
  };

  const variantClasses = {
    default: "bg-gray-100 text-gray-800",
    success: "bg-green-100 text-green-800",
    warning: "bg-yellow-100 text-yellow-800",
    error: "bg-red-100 text-red-800",
    info: "bg-blue-100 text-blue-800"
  };

  return (
    <span className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]}`}>
      {children}
    </span>
  );
}

function UpcomingAppointments({
  selectedPatient,
}: {
  selectedPatient: string | null;
}) {
  const { appointments = [] } = useUpcomingAppointments({
    selectedPatient,
  });

  const appointmentList = Array.isArray(appointments) ? appointments : [];

  const now = new Date();
  const futureAppointments = appointmentList.filter((apmt) => {
    const startTime = new Date(apmt?.endTime);
    const status: string = apmt?.isConsultationCompleted;
    return isAfter(startTime, now) && status !== "COMPLETED";
  });

  if (!futureAppointments.length) return null;

  const getAppointmentTypeInfo = (type: string) => {
    switch (type) {
      case "WALK_IN":
        return {
          label: "In-Clinic",
          variant: "info" as const,
          icon: <Hospital size={12} />
        };
      case "TELECONSULTATION":
        return {
          label: "Teleconsultation",
          variant: "info" as const,
          icon: <Video size={12} />
        };
      default:
        return {
          label: type || "Standard",
          variant: "default" as const,
          icon: null
        };
    }
  };

  const getAppointmentStatusInfo = (status: string, isCompleted: string, appointmentType: string) => {
    if (isCompleted === "COMPLETED") {
      return { label: "Completed", variant: "success" as const };
    }

    // Special case: TELECONSULTATION appointments with BOOKED status should show as PENDING
    if (appointmentType === "TELECONSULTATION" && status === "BOOKED") {
      return { label: "Pending", variant: "warning" as const };
    }

    switch (status) {
      case "CONFIRMED":
      case "BOOKED":
        return { label: "Booked", variant: "success" as const };
      case "ACCEPTED":
        return { label: "Accepted", variant: "success" as const };
      case "PENDING":
        return { label: "Pending", variant: "warning" as const };
      case "CANCELLED":
        return { label: "Cancelled", variant: "error" as const };
      case "RESCHEDULED":
        return { label: "Rescheduled", variant: "warning" as const };
      case "REJECTED":
        return { label: "Rejected", variant: "error" as const };
      default:
        return { label: status || "Scheduled", variant: "default" as const };
    }
  };

  const formatBookingId = (bookingId: string | number | undefined) => {
    if (!bookingId) return "0000";
    const idStr = bookingId.toString();
    // Show full ID with proper formatting
    return idStr.length > 4 ? idStr : idStr.padStart(4, '0');
  };

  return (
    <div className="mt-4">
      <h2 className="mb-2 font-bold sm:mb-4 sm:text-lg">
        Upcoming Appointments
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 md:gap-6 xl:grid-cols-3">
        {futureAppointments.map((apmt) => {
          // console.log(apmt);
          const appointmentDate = apmt?.startTime;
          const dayOfWeek = appointmentDate
            ? format(new Date(appointmentDate), "EEEE")
            : "";
          const startTime = apmt?.startTime
            ? format(new Date(apmt.startTime), "hh:mm a")
            : "";
          const endTime = apmt?.endTime
            ? format(new Date(apmt.endTime), "hh:mm a")
            : "";

          const typeInfo = getAppointmentTypeInfo(apmt.appointmentType);
          const statusInfo = getAppointmentStatusInfo(
            apmt.appointmentStatus,
            apmt.isConsultationCompleted,
            apmt.appointmentType // Pass appointmentType for the special logic
          );

          return (
            <div
              key={apmt.bookingId}
              className="rounded-lg border-4 border-primary bg-white p-5 text-primary"
            >
              {/* Badge Section - Top of card */}
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={typeInfo.variant} size="sm">
                    {typeInfo.icon}
                    {typeInfo.label}
                  </Badge>
                  <Badge variant={statusInfo.variant} size="sm">
                    {statusInfo.label}
                  </Badge>
                </div>
                {/* Enhanced Booking ID display */}
                <div className="flex-shrink-0">
                  <div className="flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1">
                    <span className="text-xs text-gray-500 font-medium">
                      Booking ID:
                    </span>
                    <span className="text-xs text-gray-800 font-bold">
                      {formatBookingId(apmt.bookingId)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Appointment Time Info */}
              <h2 className="mb-2 text-sm font-bold md:text-xl">
                {startTime} - {endTime},{" "}
                {dayOfWeek.slice(0, 3)},{" "}
                {format(new Date(apmt.appointmentDate), "dd MMM yyyy")}
              </h2>

              {/* Doctor Info */}
              <p className="text-sm font-bold md:text-lg">
                Dr {apmt.doctorName}
              </p>
              {apmt?.doctorSpecialities && apmt.doctorSpecialities.length > 0 && (
                <p className="text-xs text-black font-bold opacity-60 sm:text-sm">
                  {apmt.doctorSpecialities.join(", ")}
                </p>
              )}

              {/* Patient Name */}
              <p className="mt-2 text-xs font-medium text-gray-700 md:text-sm">
                For {apmt.patient.name}
              </p>

              {/* Actions Section - Fixed Layout */}
              <div className="mt-4 flex items-center justify-end gap-2">
                <Link
                  to={`/appointment/${apmt.bookingId}`}
                  className={buttonVariants({
                    className: "h-8 px-3 text-xs font-medium md:h-9 md:px-4 md:text-sm",
                    size: "sm",
                    variant: "default",
                  })}
                >
                  Details
                </Link>
                {(apmt.appointmentType === "WALK_IN" || apmt.appointmentType === "Walk-in") && (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`https://www.google.com/maps/search/?api=1&query=${apmt.branchDetails?.[0]?.latitude ?? 0.000000},${apmt.branchDetails?.[0]?.longitude ?? 0.000000}`}
                    className={buttonVariants({
                      className: "h-8 px-3 text-xs font-medium md:h-9 md:px-4 md:text-sm",
                      size: "sm",
                      variant: "secondary",
                    })}
                  >
                    <span className="flex items-center">
                      Directions
                      <svg
                        className="ml-1 h-4 w-4 md:ml-2 md:h-5 md:w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 20V18.6C4 15.2397 4 13.5595 4.65396 12.2761C5.2292 11.1471 6.14708 10.2292 7.27606 9.65396C8.55953 9 10.2397 9 13.6 9H20M20 9L15 14M20 9L15 4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default UpcomingAppointments;
